import { v4 as uuid } from 'uuid';
import * as BlipSdk from 'blip-sdk';
import * as WebSocketTransport from 'lime-transport-websocket';
import Semaphore from 'semaphore-async-await';
import { MonitoringErrorHandler } from 'monitoring-error-handler';
import { LimeError } from 'modules/application/misc/LimeError';
import { MessagingHubFeatures } from './MessagingHubFeatures';
class MessagingHubService {
    static $inject = ['MESSAGINGHUBDOMAIN', 'MESSAGINGHUBWEBSOCKETHOSTNAME', 'MESSAGINGHUBWEBSOCKETHOSTNAMETENANT', 'MESSAGINGHUBWEBSOCKETPORT', 'MESSAGINGHUBWEBSOCKETSCHEME', 'AuthenticationService', 'TenantService', 'COMMAND_TIMEOUT', 'BLIP_DOMAIN', 'BLIP_AZURE_ISSUER', 'BLIP_ACCOUNT_ISSUER', 'BLIP_WEBSOCKET_HOSTNAME', 'BLIP_WEBSOCKET_HOSTNAME_TENANT', 'BLIP_WEBSOCKET_PORT', 'BLIP_WEBSOCKET_SCHEME', 'UrlTenantHandlerService'];
    constructor(MESSAGINGHUBDOMAIN, MESSAGINGHUBWEBSOCKETHOSTNAME, MESSAGINGHUBWEBSOCKETHOSTNAMETENANT, MESSAGINGHUBWEBSOCKETPORT, MESSAGINGHUBWEBSOCKETSCHEME, AuthenticationService, TenantService, COMMAND_TIMEOUT, BLIP_DOMAIN, BLIP_AZURE_ISSUER, BLIP_ACCOUNT_ISSUER, BLIP_WEBSOCKET_HOSTNAME, BLIP_WEBSOCKET_HOSTNAME_TENANT, BLIP_WEBSOCKET_PORT, BLIP_WEBSOCKET_SCHEME, UrlTenantHandlerService) {
        'ngInject';
        this.MESSAGINGHUBDOMAIN = MESSAGINGHUBDOMAIN;
        this.MESSAGINGHUBWEBSOCKETHOSTNAME = MESSAGINGHUBWEBSOCKETHOSTNAME;
        this.MESSAGINGHUBWEBSOCKETHOSTNAMETENANT = MESSAGINGHUBWEBSOCKETHOSTNAMETENANT;
        this.MESSAGINGHUBWEBSOCKETPORT = MESSAGINGHUBWEBSOCKETPORT;
        this.MESSAGINGHUBWEBSOCKETSCHEME = MESSAGINGHUBWEBSOCKETSCHEME;
        this.AuthenticationService = AuthenticationService;
        this.TenantService = TenantService;
        this.COMMAND_TIMEOUT = COMMAND_TIMEOUT;
        this.BLIP_DOMAIN = BLIP_DOMAIN;
        this.BLIP_AZURE_ISSUER = BLIP_AZURE_ISSUER;
        this.BLIP_ACCOUNT_ISSUER = BLIP_ACCOUNT_ISSUER;
        this.BLIP_WEBSOCKET_HOSTNAME = BLIP_WEBSOCKET_HOSTNAME;
        this.BLIP_WEBSOCKET_HOSTNAME_TENANT = BLIP_WEBSOCKET_HOSTNAME_TENANT;
        this.BLIP_WEBSOCKET_PORT = BLIP_WEBSOCKET_PORT;
        this.BLIP_WEBSOCKET_SCHEME = BLIP_WEBSOCKET_SCHEME;
        this.UrlTenantHandlerService = UrlTenantHandlerService;
        this.extensions = {};
        this.lock = new Semaphore(1);
        const oidcData = JSON.parse(localStorage.getItem('oidc.azureB2CData'));
        this.AZURE_AD_ENABLED = !!oidcData && !!oidcData.isEnabled;
    }
    async sendMessage(message) {
        await this.client.sendMessage(message);
    }
    addCommandReceiver(predicate, commandReceiver) {
        this.client.addCommandReceiver(predicate, (command) => {
            commandReceiver(command);
        });
    }
    clearCommandReceivers() {
        this.client.clearCommandReceivers();
    }
    /**
     * Send command and return just it resource
     * @param command Lime.Command
     * @param timeout Timeout until reject command
     * @deprecated USE processCommand instead!
     */
    async sendCommand(command, timeout = this.COMMAND_TIMEOUT) {
        try {
            const requestCommand = await this.addParametersCommandRequest(command);
            this.injectAuthenticationMetadata(requestCommand);
            this.injectSaveCommandMetadata(requestCommand);
            await this.injectSaveCommandMetadataForDelegations(requestCommand);
            const { resource } = await this.client.sendCommand(requestCommand, timeout);
            return resource;
        }
        catch (e) {
            let error;
            try {
                error = JSON.parse(e);
            }
            catch (e) {
                return;
            }
            if (error.reason) {
                if (error.reason.code != 67 && //Requested resource was not found
                    error.reason.code != 62 &&
                    error.reason.code != 42 && // The inbox cannot store commands
                    error.reason.code != 11 // The session was terminated by server
                ) {
                    MonitoringErrorHandler.instance.trackException({
                        exception: e,
                    });
                }
                throw new LimeError(error.reason);
            }
        }
    }
    /**
     * Send command without sanitize it return object
     * @param command Lime.Command
     * @param timeout Timeout until reject command
     */
    async sendCommandSafe(command, timeout = this.COMMAND_TIMEOUT) {
        try {
            const requestCommand = await this.addParametersCommandRequest(command);
            this.injectAuthenticationMetadata(requestCommand);
            this.injectSaveCommandMetadata(requestCommand);
            await this.injectSaveCommandMetadataForDelegations(requestCommand);
            // Don't remove this await
            return await this.client.sendCommand(requestCommand, timeout);
        }
        catch (e) {
            const error = JSON.parse(e);
            if (error.reason) {
                throw new LimeError(error.reason);
            }
        }
    }
    /**
     * This method pass full responsability to lime-js library to use transport to send commands.
     * It will never reject promises and always resolve them. The responsability to reject or
     * throw errors is fully of project that will use blip-sdk
     * @param command Lime.Command
     * @param timeout Timeout until reject command
     */
    async processCommand(command, timeout = this.COMMAND_TIMEOUT) {
        const requestCommand = await this.addParametersCommandRequest(command);
        this.injectAuthenticationMetadata(requestCommand);
        this.injectSaveCommandMetadata(requestCommand);
        await this.injectSaveCommandMetadataForDelegations(requestCommand);
        return await this.client.processCommand(requestCommand, timeout);
    }
    async addParametersCommandRequest(command) {
        let requestCommand = {
            ...command,
            id: command.id ? command.id : uuid(),
        };
        if (!this.messagingHubCommandWithDelegation || command.uri == '/ping') {
            return requestCommand;
        }
        const to = command.to ?? `postmaster@${this.MESSAGINGHUBDOMAIN}`;
        const identifier = `${this.instanceName}@${this.MESSAGINGHUBDOMAIN}`;
        // We need to 'force' the caller here so other applications nodes receive the bot identity correctly
        // TODO: all functions calling the ConfigurationsSeervice should pass the caller parameter so we can remove this fix
        const uri = (command.method == 'set' || command.method == 'delete')
            && command.uri.endsWith('/configuration') ? `${command.uri}?caller=${identifier}` : command.uri;
        if (command.uri.startsWith('lime://') && (command.method === 'subscribe' || command.method === 'unsubscribe')) {
            return requestCommand = {
                ...requestCommand,
                to: command.to ?? to,
                from: command.from ?? this.getLocalNode(),
                uri: uri,
            };
        }
        requestCommand = {
            ...requestCommand,
            to: command.to ?? to,
            from: command.from ?? identifier,
            pp: command.pp ?? this.getLocalNode(),
            uri: uri,
        };
        return requestCommand;
    }
    /**
     * Return a command response or throw an exception if it failed.
     * @param response Command response, including status and resource/reason when available
     */
    ensureCommandSuccess(response) {
        if (response.status == 'success') {
            return response;
        }
        const { code, description } = response.reason;
        throw new LimeError({ code, description });
    }
    async closeClient(application) {
        try {
            await this.lock.wait();
            if (await this.isCurrentApplication(application)) {
                await this.client.close();
            }
        }
        finally {
            this.lock.signal();
        }
    }
    async resetClient(application) {
        try {
            await this.lock.wait();
            await this.checkFeatures();
            if (!await this.isCurrentApplication(application)) {
                if (this.client && this.messagingHubCommandWithDelegation && !this.connectionClosing()) {
                    return this.changeApplication(application);
                }
                else {
                    await this.createClient(application);
                }
            }
            return this.getCurrentClient();
        }
        finally {
            this.lock.signal();
        }
    }
    getLocalNode() {
        return this.client._clientChannel.localNode;
    }
    getCurrentClient() {
        return this.client;
    }
    connectionClosing() {
        return this.client._closing;
    }
    changeApplication(application) {
        this.setApplication(application);
        this.client.application = application;
    }
    async isCurrentApplication(application) {
        if (this.messagingHubCommandWithDelegation) {
            //With messaging hub command with delagation, this.client._application content is the logged user identity
            return this.client && this.application.shortName === application.shortName;
        }
        return this.client && this.client._application.identifier === application.shortName;
    }
    async createClient(application) {
        try {
            if (!this.messagingHubCommandWithDelegation) {
                await this.createClientMessaging(application);
            }
            else {
                await this.createClientBlip(application);
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    async createClientBlip(application) {
        this.setApplication(application);
        let client;
        try {
            client = this.buildBlipClient();
            await client.connect();
            this.client = client;
            this.client.application = application;
        }
        catch (e) {
            console.error(e);
            client._transport.onClose = () => { };
            client.close();
        }
    }
    buildBlipClient() {
        const identifier = encodeURIComponent(this.AuthenticationService.email);
        const token = this.AuthenticationService.token;
        const blipWebsocketHostname = this.UrlTenantHandlerService.addTenantPrefixToUrl(this.BLIP_WEBSOCKET_HOSTNAME_TENANT, undefined, this.BLIP_WEBSOCKET_HOSTNAME);
        return new BlipSdk.ClientBuilder()
            .withIdentifier(identifier)
            .withToken(token)
            .withDomain(this.BLIP_DOMAIN)
            .withIssuer(this.AZURE_AD_ENABLED ? this.BLIP_AZURE_ISSUER : this.BLIP_ACCOUNT_ISSUER)
            .withHostName(blipWebsocketHostname)
            .withPort(this.BLIP_WEBSOCKET_PORT)
            .withScheme(this.BLIP_WEBSOCKET_SCHEME)
            .withRoutingRule('instance')
            .withInstance(`portal-${this.instanceName}`)
            .withNotifyConsumed(false)
            .withTransportFactory(() => new WebSocketTransport())
            .build();
    }
    async createClientMessaging(application) {
        const messagingHubWebsocketHostname = this.TenantService.addTenantPrefixToUrl(this.MESSAGINGHUBWEBSOCKETHOSTNAMETENANT, application?.tenantId, this.MESSAGINGHUBWEBSOCKETHOSTNAME);
        //TODO: remove the callerApplication reference after resolve the message hub credentials in subflow applications
        const client = new BlipSdk.ClientBuilder()
            .withIdentifier(!application.isSubflow ? application.shortName : application.callerApplication.shortName)
            .withDomain(this.MESSAGINGHUBDOMAIN)
            .withAccessKey(!application.isSubflow ? application.accessKey : application.callerApplication.accessKey)
            .withHostName(messagingHubWebsocketHostname)
            .withPort(this.MESSAGINGHUBWEBSOCKETPORT)
            .withScheme(this.MESSAGINGHUBWEBSOCKETSCHEME)
            .withRoutingRule('instance')
            .withInstance(`portal-${encodeURIComponent(this.AuthenticationService.email)}`)
            .withTransportFactory(() => new WebSocketTransport())
            .build();
        await client.connect();
        this.client = client;
        this.client.application = application;
    }
    injectAuthenticationMetadata(requestCommand) {
        requestCommand.metadata = requestCommand.metadata || {};
        requestCommand.metadata['blip_portal.email'] = this.AuthenticationService.email;
    }
    injectSaveCommandMetadata(requestCommand) {
        requestCommand.metadata = requestCommand.metadata || {};
        if (requestCommand.method === 'set' ||
            requestCommand.method === 'delete' ||
            requestCommand.method === 'merge') {
            requestCommand.metadata['server.shouldStore'] = 'true';
        }
    }
    async injectSaveCommandMetadataForDelegations(requestCommand) {
        const isDelegationCommand = (requestCommand) => requestCommand.pp || requestCommand.uri.startsWith('lime://');
        if (requestCommand.method === 'get' &&
            isDelegationCommand(requestCommand) &&
            await MessagingHubFeatures.enableGetDelegationCommandsLogging()) {
            requestCommand.metadata = requestCommand.metadata || {};
            requestCommand.metadata['server.shouldStore'] = 'true';
        }
    }
    setApplication(application) {
        this.application = application;
        this.updateInstanceName();
    }
    updateInstanceName() {
        this.instanceName = !this.application.isSubflow ? this.application.shortName : this.application.callerApplication.shortName;
    }
    async checkFeatures() {
        if (this.messagingHubCommandWithDelegation === undefined) {
            this.messagingHubCommandWithDelegation = await MessagingHubFeatures.GetMessagingHubCommandWithDelegation();
        }
    }
    getExtension(type) {
        let extension = this.extensions[type];
        if (!extension) {
            extension = new type(this);
            this.extensions[type] = extension;
        }
        return extension;
    }
    get ArtificialIntelligence() {
        const artificialIntelligenceExtension = this.getExtension(BlipSdk.Extensions.ArtificialIntelligence);
        artificialIntelligenceExtension._to = `postmaster@ai.${this.MESSAGINGHUBDOMAIN}`;
        return artificialIntelligenceExtension;
    }
    get Chat() {
        return this.client.Chat;
    }
}
MessagingHubService.$inject = ['MESSAGINGHUBDOMAIN', 'MESSAGINGHUBWEBSOCKETHOSTNAME', 'MESSAGINGHUBWEBSOCKETHOSTNAMETENANT', 'MESSAGINGHUBWEBSOCKETPORT', 'MESSAGINGHUBWEBSOCKETSCHEME', 'AuthenticationService', 'TenantService', 'COMMAND_TIMEOUT', 'BLIP_DOMAIN', 'BLIP_AZURE_ISSUER', 'BLIP_ACCOUNT_ISSUER', 'BLIP_WEBSOCKET_HOSTNAME', 'BLIP_WEBSOCKET_HOSTNAME_TENANT', 'BLIP_WEBSOCKET_PORT', 'BLIP_WEBSOCKET_SCHEME', 'UrlTenantHandlerService'];
export default MessagingHubService;
